import React, { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  LinearProgress,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  GroupAdd as GroupAddIcon,
  PersonRemove as PersonRemoveIcon,
  PeopleAlt as PeopleAltIcon,
  Payments as PaymentsIcon,
  Badge as BadgeIcon,
  PersonOff as PersonOffIcon,
  Egg as EggIcon,
} from '@mui/icons-material';
import { useBirdBatches } from '../hooks/useBirds';
import { useEmployees } from '../hooks/useEmployee';
import {
  useBatchEmployees,
  useAssignEmployeesToBatch,
  useReleaseEmployeeFromBatch,
} from '../hooks/useBatchEmployees';
import { BatchEmployeeAssignment } from '../types';
import { format } from 'date-fns';

// ─── Helpers ───────────────────────────────────────────────────────────────────

function initials(name: string) {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

// ─── Design tokens ─────────────────────────────────────────────────────────────

const inputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#0a0a0c',
    color: '#e0e0e0',
    borderRadius: 1.5,
    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
    '&:hover fieldset': { borderColor: 'rgba(255,152,0,0.4)' },
    '&.Mui-focused fieldset': { borderColor: '#ff9800' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#ff9800' },
};

const dataGridSx = {
  border: 'none',
  background: 'transparent',
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    '&:focus, &:focus-within': { outline: 'none' },
  },
  '& .MuiDataGrid-columnHeaders': {
    background: 'rgba(255,152,0,0.06)',
    borderBottom: '1px solid rgba(255,152,0,0.15)',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 700,
    fontSize: '0.68rem',
    letterSpacing: '0.7px',
    color: 'rgba(255,152,0,0.65)',
    textTransform: 'uppercase',
  },
  '& .MuiDataGrid-row': {
    minHeight: '72px !important',
    '&:hover': { background: 'rgba(255,152,0,0.04)' },
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid rgba(255,152,0,0.1)',
    background: 'rgba(255,152,0,0.03)',
  },
  '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
    { color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem' },
  '& .MuiIconButton-root': { color: 'rgba(255,255,255,0.35)' },
  '& .MuiIconButton-root.Mui-disabled': { color: 'rgba(255,255,255,0.12)' },
  '& .MuiDataGrid-virtualScroller': { minHeight: 160 },
};

// ─── StatCard ──────────────────────────────────────────────────────────────────

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: string;
  accent: string;
  Icon: React.ElementType;
}> = ({ label, value, sub, accent, Icon }) => (
  <Paper
    elevation={0}
    sx={{
      flex: 1,
      minWidth: 160,
      p: 2.5,
      background: '#111318',
      border: '1px solid rgba(255,255,255,0.05)',
      borderLeft: `3px solid ${accent}`,
      borderRadius: 2.5,
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <Icon
      sx={{
        position: 'absolute',
        right: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: 52,
        color: `${accent}0D`,
      }}
    />
    <Typography
      variant="caption"
      sx={{
        color: 'rgba(255,255,255,0.35)',
        letterSpacing: '0.7px',
        fontSize: '0.62rem',
        textTransform: 'uppercase',
        display: 'block',
        mb: 0.75,
      }}
    >
      {label}
    </Typography>
    <Typography
      fontWeight={800}
      sx={{
        color: accent,
        lineHeight: 1,
        fontSize: typeof value === 'number' ? '2rem' : '1.2rem',
        mb: sub ? 0.5 : 0,
      }}
    >
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.28)', fontSize: '0.65rem' }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

// ─── Main ──────────────────────────────────────────────────────────────────────

const BatchEmployeeAssignments: React.FC = () => {
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [assignDialog, setAssignDialog] = useState(false);
  const [releaseTarget, setReleaseTarget] = useState<BatchEmployeeAssignment | null>(null);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([]);

  const { data: batchesData, isLoading: batchesLoading } = useBirdBatches({ status: 'active' });
  const { data: allEmployees } = useEmployees({ is_active: 1, per_page: 200 });

  const {
    data: batchEmployeesData,
    isLoading: employeesLoading,
    isFetching,
    error,
    refetch,
  } = useBatchEmployees(selectedBatchId);

  const assignMutation = useAssignEmployeesToBatch(selectedBatchId ?? 0);
  const releaseMutation = useReleaseEmployeeFromBatch(selectedBatchId ?? 0);

  const batches = batchesData?.data ?? [];
  const assignments: BatchEmployeeAssignment[] = batchEmployeesData?.assignments ?? [];
  const totalCost: string = batchEmployeesData?.total_salary_cost ?? 'GH₵ 0.00';

  const assignedEmployeeIds = new Set(assignments.map((a) => a.employee.id));
  const availableEmployees = (allEmployees?.data ?? []).filter(
    (e) => !assignedEmployeeIds.has(e.id)
  );
  const selectedBatch = batches.find((b: any) => b.id === selectedBatchId);

  const canAssign = !!selectedBatchId && availableEmployees.length > 0;

  const handleAssign = async () => {
    if (!selectedEmployeeIds.length || !selectedBatchId) return;
    try {
      await assignMutation.mutateAsync({ employee_ids: selectedEmployeeIds });
      setAssignDialog(false);
      setSelectedEmployeeIds([]);
    } catch (e) {
      console.error(e);
    }
  };

  const handleRelease = async () => {
    if (!releaseTarget || !selectedBatchId) return;
    try {
      await releaseMutation.mutateAsync(releaseTarget.employee.id);
      setReleaseTarget(null);
    } catch (e) {
      console.error(e);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'employee',
      headerName: 'Employee',
      flex: 2,
      minWidth: 220,
      renderCell: (params: GridRenderCellParams<BatchEmployeeAssignment>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
          <Avatar
            sx={{
              bgcolor: 'rgba(255,152,0,0.12)',
              color: '#ff9800',
              width: 40,
              height: 40,
              fontSize: '0.88rem',
              fontWeight: 800,
              border: '1.5px solid rgba(255,152,0,0.25)',
              flexShrink: 0,
            }}
          >
            {initials(params.row.employee.name)}
          </Avatar>
          <Box>
            <Typography
              sx={{ color: '#fff', fontWeight: 700, fontSize: '0.88rem', lineHeight: 1.3 }}
            >
              {params.row.employee.name}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.38)', fontSize: '0.72rem', mt: 0.3 }}>
              {params.row.employee.email ?? params.row.employee.phone ?? '—'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'salary',
      headerName: 'Monthly Salary',
      flex: 1,
      minWidth: 150,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams<BatchEmployeeAssignment>) => (
        <Chip
          label={params.row.employee.salary}
          size="small"
          sx={{
            bgcolor: 'rgba(255,152,0,0.1)',
            color: '#ffa726',
            fontWeight: 700,
            fontSize: '0.78rem',
            border: '1px solid rgba(255,152,0,0.2)',
          }}
        />
      ),
    },
    {
      field: 'active_batch_count',
      headerName: 'Batches',
      flex: 0.7,
      minWidth: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams<BatchEmployeeAssignment>) => (
        <Chip
          label={`${params.row.active_batch_count} batch${params.row.active_batch_count !== 1 ? 'es' : ''}`}
          size="small"
          sx={{
            bgcolor: 'rgba(33,150,243,0.1)',
            color: '#64b5f6',
            fontWeight: 600,
            fontSize: '0.72rem',
            border: '1px solid rgba(33,150,243,0.2)',
          }}
        />
      ),
    },
    {
      field: 'allocated_salary',
      headerName: 'Allocated Here',
      flex: 1,
      minWidth: 150,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams<BatchEmployeeAssignment>) => (
        <Chip
          label={params.row.allocated_salary}
          size="small"
          sx={{
            bgcolor: 'rgba(76,175,80,0.1)',
            color: '#81c784',
            fontWeight: 700,
            fontSize: '0.78rem',
            border: '1px solid rgba(76,175,80,0.2)',
          }}
        />
      ),
    },
    {
      field: 'assigned_at',
      headerName: 'Since',
      flex: 0.85,
      minWidth: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams<BatchEmployeeAssignment>) => (
        <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.8rem' }}>
          {params.row.assigned_at ? format(new Date(params.row.assigned_at), 'MMM dd, yyyy') : '—'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      flex: 0.5,
      minWidth: 64,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params: GridRenderCellParams<BatchEmployeeAssignment>) => (
        <Tooltip title="Release from batch" arrow>
          <IconButton
            size="small"
            onClick={() => setReleaseTarget(params.row)}
            sx={{
              color: '#f44336',
              bgcolor: 'rgba(244,67,54,0.08)',
              border: '1px solid rgba(244,67,54,0.18)',
              width: 32,
              height: 32,
              '&:hover': { bgcolor: 'rgba(244,67,54,0.18)', borderColor: 'rgba(244,67,54,0.35)' },
            }}
          >
            <PersonRemoveIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {(isFetching || batchesLoading) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            bgcolor: 'rgba(255,152,0,0.1)',
            '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #ff9800, #ffa726)' },
          }}
        />
      )}

      {/* ── Header ── */}
      <Box
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          background: 'linear-gradient(135deg, #131316 0%, #1c1c22 100%)',
          border: '1px solid rgba(255,152,0,0.15)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
          display: 'flex',
          alignItems: 'center',
          gap: 2.5,
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{
            width: 54,
            height: 54,
            borderRadius: 2,
            flexShrink: 0,
            background:
              'linear-gradient(135deg, rgba(255,152,0,0.2) 0%, rgba(255,152,0,0.08) 100%)',
            border: '1px solid rgba(255,152,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <BadgeIcon sx={{ color: '#ff9800', fontSize: 28 }} />
        </Box>
        <Box flex={1} minWidth={0}>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{ color: '#fff', letterSpacing: '-0.02em', lineHeight: 1.2 }}
          >
            Batch Employee Assignments
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.38)', mt: 0.3 }}>
            Assign employees to batches · salary is split proportionally across all assigned batches
          </Typography>
        </Box>
        <Tooltip
          title={
            !selectedBatchId
              ? 'Select a batch first'
              : availableEmployees.length === 0
                ? 'All employees already assigned'
                : ''
          }
          arrow
        >
          <span>
            <Button
              variant="contained"
              startIcon={<GroupAddIcon />}
              onClick={() => setAssignDialog(true)}
              disabled={!canAssign}
              sx={{
                background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                color: '#000',
                fontWeight: 700,
                borderRadius: 2,
                px: 2.5,
                boxShadow: '0 4px 18px rgba(255,152,0,0.35)',
                flexShrink: 0,
                '&:hover': { background: 'linear-gradient(135deg, #ffa726 0%, #ff9800 100%)' },
                '&:disabled': {
                  background: 'rgba(255,152,0,0.18)',
                  color: 'rgba(255,255,255,0.25)',
                },
              }}
            >
              Assign Employee
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* ── Batch selector ── */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 2.5,
          border: '1px solid rgba(255,152,0,0.12)',
          background: '#0e0e10',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderBottom: '1px solid rgba(255,255,255,0.04)',
            background: 'rgba(255,152,0,0.04)',
          }}
        >
          <Typography
            sx={{
              color: 'rgba(255,152,0,0.6)',
              fontSize: '0.62rem',
              fontWeight: 700,
              letterSpacing: '0.7px',
              textTransform: 'uppercase',
            }}
          >
            Select Batch
          </Typography>
        </Box>
        <Box sx={{ p: 2.5, display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Box flex={1} minWidth={260}>
            <Autocomplete
              options={batches}
              getOptionLabel={(b: any) => b.identifier ?? `Batch #${b.id}`}
              value={selectedBatch ?? null}
              onChange={(_, newVal) => setSelectedBatchId(newVal?.id ?? null)}
              loading={batchesLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Choose a batch to manage employees…"
                  size="small"
                  sx={inputSx}
                />
              )}
              renderOption={(props, option: any) => (
                <Box
                  component="li"
                  {...props}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: '10px !important' }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      bgcolor: 'rgba(255,152,0,0.1)',
                      border: '1px solid rgba(255,152,0,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <EggIcon sx={{ color: '#ff9800', fontSize: 16 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#fff' }}>
                      {option.identifier ?? `Batch #${option.id}`}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' }}>
                      {[
                        option.date_delivered && `Delivered ${option.date_delivered}`,
                        option.total_quantity &&
                          `${Number(option.total_quantity).toLocaleString()} birds`,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          </Box>

          {/* Batch summary pill when selected */}
          {selectedBatch && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', pt: 0.5 }}>
              {selectedBatch.total_quantity && (
                <Chip
                  label={`${Number(selectedBatch.total_quantity).toLocaleString()} birds`}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,152,0,0.1)',
                    color: '#ffa726',
                    fontWeight: 600,
                    border: '1px solid rgba(255,152,0,0.2)',
                  }}
                />
              )}
              {selectedBatch.date_delivered && (
                <Chip
                  label={`Delivered ${selectedBatch.date_delivered}`}
                  size="small"
                  sx={{ bgcolor: 'rgba(33,150,243,0.08)', color: '#64b5f6', fontSize: '0.72rem' }}
                />
              )}
              {assignments.length > 0 && (
                <AvatarGroup
                  max={4}
                  sx={{
                    '& .MuiAvatar-root': {
                      width: 26,
                      height: 26,
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      bgcolor: 'rgba(255,152,0,0.15)',
                      color: '#ffa726',
                      border: '1.5px solid rgba(255,152,0,0.25)',
                    },
                  }}
                >
                  {assignments.map((a) => (
                    <Tooltip key={a.employee.id} title={a.employee.name} arrow>
                      <Avatar>{initials(a.employee.name)}</Avatar>
                    </Tooltip>
                  ))}
                </AvatarGroup>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* ── Stats ── */}
      {selectedBatchId && batchEmployeesData && (
        <Box display="flex" gap={2.5} mb={3} flexWrap="wrap">
          <StatCard
            label="Employees Assigned"
            value={assignments.length}
            sub={`to ${selectedBatch?.identifier ?? 'this batch'}`}
            accent="#ff9800"
            Icon={PeopleAltIcon}
          />
          <StatCard
            label="Monthly Salary Cost"
            value={totalCost}
            sub="total for this batch"
            accent="#66bb6a"
            Icon={PaymentsIcon}
          />
          <StatCard
            label="Available to Assign"
            value={availableEmployees.length}
            sub="active employees not yet on batch"
            accent={availableEmployees.length > 0 ? '#42a5f5' : 'rgba(255,255,255,0.2)'}
            Icon={GroupAddIcon}
          />
          <StatCard
            label="Total Active Employees"
            value={(allEmployees?.data ?? []).length}
            sub="across all batches"
            accent="rgba(255,255,255,0.25)"
            Icon={BadgeIcon}
          />
        </Box>
      )}

      {/* ── No batch selected ── */}
      {!selectedBatchId && (
        <Paper
          elevation={0}
          sx={{
            py: 8,
            px: 4,
            textAlign: 'center',
            border: '1px dashed rgba(255,152,0,0.15)',
            borderRadius: 3,
            background: 'rgba(255,152,0,0.02)',
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: 'rgba(255,152,0,0.07)',
              border: '1px solid rgba(255,152,0,0.14)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2.5,
            }}
          >
            <BadgeIcon sx={{ fontSize: 36, color: 'rgba(255,152,0,0.35)' }} />
          </Box>
          <Typography
            sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '1rem', fontWeight: 600, mb: 0.75 }}
          >
            No batch selected
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.22)', fontSize: '0.82rem' }}>
            Pick a batch from the selector above to view and manage its employee assignments
          </Typography>
        </Paper>
      )}

      {/* ── Error ── */}
      {error && selectedBatchId && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          action={
            <Button size="small" color="inherit" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Failed to load assignments
        </Alert>
      )}

      {/* ── Table ── */}
      {selectedBatchId && !error && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: '1px solid rgba(255,152,0,0.12)',
            background: '#0e0e10',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              borderBottom: '1px solid rgba(255,152,0,0.1)',
              background: 'rgba(255,152,0,0.04)',
            }}
          >
            <PeopleAltIcon sx={{ color: '#ff9800', fontSize: 18 }} />
            <Typography fontWeight={700} sx={{ color: '#fff', fontSize: '0.9rem' }}>
              Assigned Employees
            </Typography>
            <Chip
              label={assignments.length}
              size="small"
              sx={{
                bgcolor: 'rgba(255,152,0,0.12)',
                color: '#ff9800',
                fontWeight: 700,
                height: 20,
                fontSize: '0.72rem',
              }}
            />
            <Box flex={1} />
            {availableEmployees.length === 0 && assignments.length > 0 && (
              <Chip
                label="All employees assigned"
                size="small"
                sx={{
                  bgcolor: 'rgba(76,175,80,0.1)',
                  color: '#81c784',
                  fontSize: '0.72rem',
                  border: '1px solid rgba(76,175,80,0.2)',
                }}
              />
            )}
            <Button
              size="small"
              startIcon={<GroupAddIcon sx={{ fontSize: '14px !important' }} />}
              onClick={() => setAssignDialog(true)}
              disabled={!canAssign}
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'none',
                color: canAssign ? '#ff9800' : 'rgba(255,255,255,0.2)',
                border: `1px solid ${canAssign ? 'rgba(255,152,0,0.3)' : 'rgba(255,255,255,0.08)'}`,
                borderRadius: 1.5,
                px: 1.5,
                '&:hover': { bgcolor: 'rgba(255,152,0,0.08)' },
              }}
            >
              Assign
            </Button>
          </Box>

          {/* Empty-with-batch state */}
          {!employeesLoading && assignments.length === 0 && (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <PersonOffIcon sx={{ fontSize: 44, color: 'rgba(255,255,255,0.12)', mb: 1.5 }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 600, mb: 0.5 }}>
                No employees assigned yet
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.22)', fontSize: '0.82rem', mb: 2.5 }}>
                Assign employees to start tracking salary costs for this batch
              </Typography>
              <Button
                variant="contained"
                startIcon={<GroupAddIcon />}
                onClick={() => setAssignDialog(true)}
                disabled={availableEmployees.length === 0}
                sx={{
                  background: 'linear-gradient(135deg, #ff9800, #f57c00)',
                  color: '#000',
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 2,
                  '&:hover': { background: 'linear-gradient(135deg, #ffa726, #ff9800)' },
                }}
              >
                Assign First Employee
              </Button>
            </Box>
          )}

          {assignments.length > 0 && (
            <Box sx={{ height: 'calc(100vh - 520px)', minHeight: 200 }}>
              <DataGrid
                rows={assignments}
                getRowId={(row) => row.assignment_id}
                columns={columns}
                loading={employeesLoading}
                pageSizeOptions={[25, 50]}
                disableRowSelectionOnClick
                getRowHeight={() => 'auto'}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                sx={dataGridSx}
              />
            </Box>
          )}
        </Paper>
      )}

      {/* ══ Assign dialog ══ */}
      <Dialog
        open={assignDialog}
        onClose={() => {
          setAssignDialog(false);
          setSelectedEmployeeIds([]);
        }}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              background: '#0e0e10',
              backgroundImage: 'none',
              border: '1px solid rgba(255,152,0,0.2)',
            },
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            borderBottom: '1px solid rgba(255,152,0,0.1)',
            background: 'linear-gradient(135deg, rgba(255,152,0,0.06) 0%, transparent 100%)',
          }}
        >
          <Typography variant="h6" fontWeight={800} sx={{ color: '#fff' }}>
            Assign Employees
          </Typography>
          <Typography variant="caption" sx={{ color: '#ff9800', fontWeight: 600 }}>
            {selectedBatch?.identifier ?? 'Batch'}
            {selectedBatch?.total_quantity &&
              ` · ${Number(selectedBatch.total_quantity).toLocaleString()} birds`}
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <Autocomplete
            multiple
            options={availableEmployees}
            getOptionLabel={(e) => `${e.first_name} ${e.last_name}`}
            value={availableEmployees.filter((e) => selectedEmployeeIds.includes(e.id))}
            onChange={(_, newVal) => setSelectedEmployeeIds(newVal.map((e) => e.id))}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: '10px !important' }}
              >
                <Avatar
                  sx={{
                    bgcolor: 'rgba(255,152,0,0.12)',
                    color: '#ff9800',
                    width: 34,
                    height: 34,
                    fontSize: '0.8rem',
                    fontWeight: 800,
                    border: '1.5px solid rgba(255,152,0,0.2)',
                    flexShrink: 0,
                  }}
                >
                  {initials(`${option.first_name} ${option.last_name}`)}
                </Avatar>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#fff' }}>
                    {option.first_name} {option.last_name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' }}>
                    {option.salary}
                    {option.email ? ` · ${option.email}` : ''}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Employees"
                placeholder="Search and select employees…"
                required
                sx={inputSx}
              />
            )}
            slotProps={{
              chip: {
                size: 'small',
                sx: {
                  bgcolor: 'rgba(255,152,0,0.15)',
                  color: '#ffa726',
                  fontWeight: 600,
                  border: '1px solid rgba(255,152,0,0.25)',
                },
              },
            }}
          />
          {selectedEmployeeIds.length > 0 && (
            <Typography
              variant="caption"
              sx={{ mt: 1.5, display: 'block', color: 'rgba(255,255,255,0.38)' }}
            >
              Salary is split equally across all batches each employee is assigned to.
            </Typography>
          )}
        </DialogContent>
        <DialogActions
          sx={{ px: 3, pb: 2.5, gap: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}
        >
          <Button
            onClick={() => {
              setAssignDialog(false);
              setSelectedEmployeeIds([]);
            }}
            sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={!selectedEmployeeIds.length || assignMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              color: '#000',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 1.5,
              px: 3,
              '&:hover': { background: 'linear-gradient(135deg, #ffa726 0%, #ff9800 100%)' },
              '&:disabled': { background: 'rgba(255,152,0,0.2)', color: 'rgba(255,255,255,0.3)' },
            }}
          >
            {assignMutation.isPending
              ? 'Assigning…'
              : `Assign${selectedEmployeeIds.length ? ` (${selectedEmployeeIds.length})` : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ Release dialog ══ */}
      <Dialog
        open={!!releaseTarget}
        onClose={() => setReleaseTarget(null)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              background: '#0e0e10',
              backgroundImage: 'none',
              border: '1px solid rgba(244,67,54,0.25)',
            },
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            borderBottom: '1px solid rgba(244,67,54,0.12)',
            background: 'linear-gradient(135deg, rgba(244,67,54,0.07) 0%, transparent 100%)',
          }}
        >
          <Typography variant="h6" fontWeight={800} sx={{ color: '#f44336' }}>
            Release Employee
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
            Salary allocation for this batch will be removed
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 2.5 }}>
          {releaseTarget && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: 'rgba(244,67,54,0.06)',
                borderRadius: 2,
                border: '1px solid rgba(244,67,54,0.18)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Avatar
                sx={{
                  bgcolor: 'rgba(244,67,54,0.15)',
                  color: '#f44336',
                  width: 40,
                  height: 40,
                  fontSize: '0.88rem',
                  fontWeight: 800,
                  border: '1.5px solid rgba(244,67,54,0.25)',
                  flexShrink: 0,
                }}
              >
                {initials(releaseTarget.employee.name)}
              </Avatar>
              <Box>
                <Typography fontWeight={700} sx={{ color: '#fff', fontSize: '0.95rem' }}>
                  {releaseTarget.employee.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>
                  {releaseTarget.employee.salary} / month
                  {releaseTarget.employee.email ? ` · ${releaseTarget.employee.email}` : ''}
                </Typography>
              </Box>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, borderTop: '1px solid rgba(244,67,54,0.1)' }}>
          <Button
            onClick={() => setReleaseTarget(null)}
            sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRelease}
            disabled={releaseMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
              color: '#fff',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 1.5,
              px: 3,
              '&:hover': { background: 'linear-gradient(135deg, #ef5350 0%, #f44336 100%)' },
            }}
          >
            {releaseMutation.isPending ? 'Releasing…' : 'Release'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BatchEmployeeAssignments;
